import { useQuery } from '@apollo/client';

import { EmptyState } from '@ovComponents/2-component/emptyState/emptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { UserContext } from '@providers/userContextProvider';
import type { ColumnDef } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTradingMutation } from '../hooks/useTradingMutation';
import { SortIndicator, useTradingTable } from '../hooks/useTradingTable';
import { FETCH_BULK_TRADE_RUNS, FETCH_PORTFOLIO_OPTIMIZERS } from '../queries';
import {
  BORDERED_BOX_SX,
  LABEL_SX,
  MICRO_SX,
  TABLE_HEAD_SX,
  VALUE_SX,
} from '../styles';
import { useTradingHub } from '../tradingHubContext';
import type { BulkTradeRunState, OptimizerState } from '../types';
import {
  BULK_TRADE_RUN_STATE_LABELS,
  getBulkTradeRunProgress,
  getNextActionLabel,
  getOptimizerProgress,
  OPTIMIZER_STATE_LABELS,
  relativeTime,
} from '../utils';
import {
  GENERATE_BULK_TRADES,
  RUN_PORTFOLIO_OPTIMIZER,
} from '../utils/mutations';
import OperationDetailPanel, {
  type NormalizedOp,
} from './operationDetailPanel';

const PipelineBar = ({
  progress,
  isFailed,
  theme,
}: {
  progress: number;
  state: string;
  isActive: boolean;
  isFailed: boolean;
  theme: Theme;
}) => {
  const barColor = isFailed
    ? theme.color.negative
    : progress >= 100
      ? theme.color.positive
      : theme.color.primary;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 100,
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: 6,
          bgcolor: theme.color.disabled,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${progress}%`,
            height: '100%',
            borderRadius: 3,
            bgcolor: barColor,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
      <Typography
        variant='labelSmall'
        sx={{
          ...VALUE_SX,
          fontWeight: 700,
          minWidth: 30,
          textAlign: 'right',
          color: barColor,
        }}
      >
        {progress}%
      </Typography>
    </Box>
  );
};

const FilterChip = ({
  label,
  selected,
  onClick,
  theme,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  theme: Theme;
}) => (
  <Box
    onClick={onClick}
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: '4px',
      cursor: 'pointer',
      userSelect: 'none',
      bgcolor: selected ? theme.color.primary : 'transparent',
      '&:hover': {
        bgcolor: selected ? theme.color.primary : theme.color.neutralHover,
      },
      transition: 'background 0.1s',
    }}
  >
    <Typography
      variant='labelSmall'
      sx={{
        ...LABEL_SX,
        color: selected ? theme.color.onPrimary : theme.color.onSurfaceVariant,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const TypeBadge = ({
  type,
  theme,
}: {
  type: 'optimizer' | 'bulkTradeRun';
  theme: Theme;
}) => {
  const isRebal = type === 'optimizer';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        px: 0.75,
        py: 0.25,
        borderRadius: '4px',
        bgcolor: isRebal
          ? theme.color.warningVariant
          : theme.color.primaryVariant,
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{
          ...TABLE_HEAD_SX,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: isRebal ? theme.color.warning : theme.color.primary,
        }}
      >
        {isRebal ? 'REBAL' : 'BATCH'}
      </Typography>
    </Box>
  );
};

const StatusBadge = ({
  label,
  isActive,
  isFailed,
  isComplete,
  theme,
}: {
  label: string;
  isActive: boolean;
  isFailed: boolean;
  isComplete: boolean;
  theme: Theme;
}) => {
  const bg = isActive
    ? theme.color.primaryVariant
    : isFailed
      ? theme.color.negativeVariant
      : isComplete
        ? theme.color.positiveVariant
        : theme.color.neutralHover;
  const color = isActive
    ? theme.color.primary
    : isFailed
      ? theme.color.negative
      : isComplete
        ? theme.color.positive
        : theme.color.onSurfaceVariant;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        px: 0.75,
        py: 0.25,
        borderRadius: '4px',
        bgcolor: bg,
      }}
    >
      <Typography variant='labelSmall' sx={{ ...LABEL_SX, color }}>
        {label}
      </Typography>
    </Box>
  );
};

const OperationsTab = () => {
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const {
    refreshKey,
    triggerRefresh,
    pendingBulkTradeRunId,
    setPendingBulkTradeRunId,
    operationsPreFilter,
    setOperationsPreFilter,
    navigateTo,
  } = useTradingHub();
  const { enqueueSnackbar } = useSnackbar();
  const [generateBulkTrades, { loading: generating }] =
    useTradingMutation(GENERATE_BULK_TRADES);
  const [typeFilter, setTypeFilter] = useState<
    'ALL' | 'OPTIMIZER' | 'BULK_TRADE_RUN'
  >('ALL');
  const [timeFilter, setTimeFilter] = useState<'ALL' | 'TODAY' | 'WEEK'>('ALL');
  const [showCompleted, setShowCompleted] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showConfirmGenerate, setShowConfirmGenerate] = useState(false);
  const [showOptimizerModal, setShowOptimizerModal] = useState(false);
  const [optimizerType, setOptimizerType] = useState<'TRANSFER' | 'DRIFT'>(
    'TRANSFER',
  );
  const [runOptimizer, { loading: runningOptimizer }] = useTradingMutation(
    RUN_PORTFOLIO_OPTIMIZER,
  );

  const {
    loading: loadingOpts,
    data: optsData,
    refetch: refetchOpts,
  } = useQuery(FETCH_PORTFOLIO_OPTIMIZERS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          sortField: 'createdAt',
          sortDesc: false,
          perPage: 50,
          offSet: 0,
        },
      },
    },
    skip: !activeOrganization?.id,
  });
  const {
    loading: loadingRuns,
    data: runsData,
    refetch: refetchRuns,
  } = useQuery(FETCH_BULK_TRADE_RUNS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          sortField: 'createdAt',
          sortDesc: true,
          perPage: 50,
          offSet: 0,
        },
      },
    },
    skip: !activeOrganization?.id,
  });

  useEffect(() => {
    if (refreshKey > 0) {
      refetchOpts();
      refetchRuns();
    }
  }, [refreshKey, refetchOpts, refetchRuns]);

  useEffect(() => {
    if (pendingBulkTradeRunId) {
      setExpandedId(pendingBulkTradeRunId);
      setPendingBulkTradeRunId(null);
    }
  }, [pendingBulkTradeRunId, setPendingBulkTradeRunId]);

  // Consume pre-filter from insights sidebar
  useEffect(() => {
    if (operationsPreFilter) {
      if (operationsPreFilter === 'ACTIVE') {
        setShowCompleted(false);
      }
      setOperationsPreFilter(null);
    }
  }, [operationsPreFilter, setOperationsPreFilter]);

  const handleGenerateBatch = async () => {
    setShowConfirmGenerate(false);
    try {
      const result = await generateBulkTrades({
        variables: {
          input: {
            organizationId: activeOrganization?.id,
          },
        },
      });
      const btrId = result.data?.generateBulkTrades?.bulkTradeRun?.id;
      if (btrId) {
        enqueueSnackbar('Trade batch generated successfully', {
          variant: 'success',
        });
        setPendingBulkTradeRunId(btrId);
        setExpandedId(btrId);
        triggerRefresh();
        refetchRuns();
      }
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleRunOptimizer = async () => {
    setShowOptimizerModal(false);
    try {
      const result = await runOptimizer({
        variables: {
          input: {
            organizationId: activeOrganization?.id,
            type: optimizerType,
          },
        },
      });
      const optId = result.data?.runPortfolioOptimizer?.portfolioOptimizer?.id;
      if (optId) {
        enqueueSnackbar('Optimizer run started', { variant: 'success' });
        setExpandedId(optId);
        triggerRefresh();
        refetchOpts();
      }
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  // Normalize all operations into a single list
  const allOps: NormalizedOp[] = useMemo(() => {
    const optimizers = (
      optsData?.fetchPortfolioOptimizers?.portfolioOptimizers || []
    ).map((o: any) => {
      const s = o.state as OptimizerState;
      const isComplete = s === 'COMPLETED';
      const isFailed = s === 'FAILED' || s === 'CANCELED';
      return {
        id: o.id,
        type: 'optimizer' as const,
        state: s,
        label: OPTIMIZER_STATE_LABELS[s] || s,
        progress: getOptimizerProgress(s),
        isActive: !isComplete && !isFailed,
        isComplete,
        isFailed,
        createdAt: o.createdAt,
        updatedAt: o.createdAt,
        tradeCount: o.subTradeRequestsCreated || o.numPortfolios || 0,
        nextAction: isComplete ? 'Done' : isFailed ? s : 'Processing',
        raw: o,
      };
    });

    const runs = (runsData?.fetchBulkTradeRuns?.bulkTradeRuns || []).map(
      (r: any) => {
        const s = r.state as BulkTradeRunState;
        const isComplete = s === 'COMPLETED' || s === 'COMPLETED_PENDING_PTFS';
        const isFailed = s === 'CANCELED';
        const totalTrades =
          r.allTradeRequestsByExchange?.reduce(
            (sum: number, ex: any) => sum + (ex.total || 0),
            0,
          ) || 0;
        return {
          id: r.id,
          type: 'bulkTradeRun' as const,
          state: s,
          label: BULK_TRADE_RUN_STATE_LABELS[s] || s,
          progress: getBulkTradeRunProgress(s),
          isActive: !isComplete && !isFailed,
          isComplete,
          isFailed,
          createdAt: r.createdAt,
          updatedAt: r.updatedAt || r.createdAt,
          tradeCount: totalTrades,
          nextAction: isComplete
            ? 'Done'
            : isFailed
              ? 'Canceled'
              : getNextActionLabel(s),
          raw: r,
        };
      },
    );

    return [...optimizers, ...runs];
  }, [optsData, runsData]);

  const filteredOps = useMemo(() => {
    let result = allOps;
    if (typeFilter !== 'ALL')
      result = result.filter(
        (o) =>
          o.type ===
          (typeFilter === 'OPTIMIZER' ? 'optimizer' : 'bulkTradeRun'),
      );
    if (!showCompleted) result = result.filter((o) => o.isActive);
    if (timeFilter !== 'ALL') {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (timeFilter === 'WEEK') start.setDate(start.getDate() - 7);
      result = result.filter((o) => new Date(o.createdAt) >= start);
    }
    return result;
  }, [allOps, typeFilter, showCompleted, timeFilter]);

  const hasActiveOps = allOps.some((o) => o.isActive);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (hasActiveOps) {
      pollRef.current = setInterval(() => {
        refetchOpts();
        refetchRuns();
      }, 5_000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveOps, refetchOpts, refetchRuns]);

  const columns: ColumnDef<NormalizedOp>[] = useMemo(
    () => [
      { id: 'expand', header: '', enableSorting: false },
      { id: 'type', header: 'Type', accessorFn: (r) => r.type },
      { id: 'state', header: 'Status', accessorFn: (r) => r.state },
      { id: 'progress', header: 'Progress', accessorFn: (r) => r.progress },
      { id: 'createdAt', header: 'Started', accessorFn: (r) => r.createdAt },
      { id: 'trades', header: 'Trades', accessorFn: (r) => r.tradeCount },
      { id: 'action', header: 'Next Action', accessorFn: (r) => r.nextAction },
    ],

    [],
  );

  const { table } = useTradingTable({
    data: filteredOps,
    columns,
    initialSorting: [{ id: 'createdAt', desc: true }],
  });

  if (loadingOpts || loadingRuns)
    return (
      <Box>
        <Box sx={{ ...BORDERED_BOX_SX, borderRadius: 1.5, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: theme.color.neutralHover,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <Skeleton
                variant='rounded'
                width={60}
                height={24}
                sx={{ borderRadius: '6px' }}
              />

              <Skeleton
                variant='rounded'
                width={90}
                height={24}
                sx={{ borderRadius: '6px' }}
              />

              <Skeleton
                variant='rounded'
                width={80}
                height={24}
                sx={{ borderRadius: '6px' }}
              />
            </Box>
          </Box>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                gap: 2,
                px: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: theme.color.outlineVariant,
              }}
            >
              <Skeleton variant='text' width={60} />
              <Skeleton variant='text' width={80} />
              <Skeleton
                variant='rounded'
                width={60}
                height={20}
                sx={{ borderRadius: '4px' }}
              />

              <Skeleton
                variant='rectangular'
                width={120}
                height={6}
                sx={{ my: 'auto', borderRadius: 3 }}
              />

              <Skeleton variant='text' width={70} />
              <Skeleton variant='text' width={30} />
              <Skeleton
                variant='rounded'
                width={100}
                height={28}
                sx={{ borderRadius: '4px', ml: 'auto' }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.25,
          mb: 0,
          ...BORDERED_BOX_SX,
          borderRadius: '6px 6px 0 0',
          borderBottom: '1px solid',
          borderBottomColor: theme.color.outlineVariant,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {[
            { key: 'ALL' as const, label: 'All' },
            { key: 'BULK_TRADE_RUN' as const, label: 'Batches' },
            { key: 'OPTIMIZER' as const, label: 'Rebalances' },
          ].map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              selected={typeFilter === chip.key}
              onClick={() => setTypeFilter(chip.key)}
              theme={theme}
            />
          ))}
          <Box
            sx={{
              width: 1,
              height: 16,
              bgcolor: theme.color.outlineVariant,
              mx: 0.5,
            }}
          />

          {[
            { key: 'ALL' as const, label: 'All Time' },
            { key: 'WEEK' as const, label: '7 Days' },
            { key: 'TODAY' as const, label: 'Today' },
          ].map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              selected={timeFilter === chip.key}
              onClick={() => setTimeFilter(chip.key)}
              theme={theme}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            size='small'
            variant='outlined'
            disabled={runningOptimizer}
            onClick={() => setShowOptimizerModal(true)}
          >
            {runningOptimizer ? 'Starting…' : 'Run Optimizer'}
          </Button>
          <Button
            size='small'
            variant='outlined'
            disabled={generating}
            onClick={() => setShowConfirmGenerate(true)}
          >
            {generating ? 'Generating...' : 'Generate Batch'}
          </Button>
          <Typography
            variant='labelSmall'
            onClick={() => setShowCompleted(!showCompleted)}
            sx={{
              ...LABEL_SX,
              color: theme.color.onSurfaceVariant,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': { color: theme.color.onSurface },
            }}
          >
            {showCompleted ? 'Hide Completed' : 'Show All'}
          </Typography>
        </Box>
      </Box>

      <ConfirmationModal
        open={showConfirmGenerate}
        title='Generate Trade Batch'
        bodyText='This will create a new bulk trade run from all eligible sub-trade requests. Continue?'
        confirmButtonLabel={generating ? 'Generating...' : 'Confirm & Generate'}
        confirmButtonColor='primary'
        confirmButtonVariant='contained'
        loading={generating}
        onConfirm={handleGenerateBatch}
        onCancel={() => setShowConfirmGenerate(false)}
      />

      <ConfirmationModal
        open={showOptimizerModal}
        title='Run Portfolio Optimizer'
        bodyText={`Run ${optimizerType === 'TRANSFER' ? 'Transfer' : 'Drift'} optimization across all eligible portfolios in this organization.`}
        confirmButtonLabel={runningOptimizer ? 'Starting…' : 'Run Optimizer'}
        confirmButtonColor='primary'
        confirmButtonVariant='contained'
        loading={runningOptimizer}
        onConfirm={handleRunOptimizer}
        onCancel={() => setShowOptimizerModal(false)}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mt: 1,
          }}
        >
          {(['TRANSFER', 'DRIFT'] as const).map((t) => (
            <Box
              key={t}
              onClick={() => setOptimizerType(t)}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: '6px',
                cursor: 'pointer',
                border: '2px solid',
                borderColor:
                  optimizerType === t
                    ? theme.color.primary
                    : theme.color.outlineVariant,
                bgcolor:
                  optimizerType === t
                    ? theme.color.primaryVariant
                    : 'transparent',
                transition: 'all 0.1s',
                '&:hover': { borderColor: theme.color.primary },
              }}
            >
              <Typography
                variant='labelSmall'
                component='div'
                sx={{
                  ...VALUE_SX,
                  fontWeight: 700,
                  color:
                    optimizerType === t
                      ? theme.color.primary
                      : theme.color.onSurfaceVariant,
                }}
              >
                {t}
              </Typography>
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...MICRO_SX, color: theme.color.onDisabled }}
              >
                {t === 'TRANSFER'
                  ? 'Cash deposits, withdrawals, new accounts'
                  : 'Target allocation drift rebalancing'}
              </Typography>
            </Box>
          ))}
        </Box>
      </ConfirmationModal>

      <Box
        sx={{
          ...BORDERED_BOX_SX,
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          bgcolor: theme.color.surface,
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  ...LABEL_SX,
                  py: 0.75,
                  letterSpacing: '0.04em',
                  color: theme.color.onSurfaceVariant,
                },
                bgcolor: theme.color.neutralHover,
              }}
            >
              <TableCell sx={{ width: '32px' }} />
              <TableCell sx={{ width: '70px' }}>TYPE</TableCell>
              <TableHeadCell
                sx={{ width: 'auto', cursor: 'pointer' }}
                onClick={table.getColumn('state')?.getToggleSortingHandler()}
              >
                STATUS
                <SortIndicator
                  sorted={table.getColumn('state')?.getIsSorted() || false}
                />
              </TableHeadCell>
              <TableCell sx={{ width: '130px' }}>PROGRESS</TableCell>
              <TableHeadCell
                sx={{ width: '100px', cursor: 'pointer' }}
                onClick={table
                  .getColumn('createdAt')
                  ?.getToggleSortingHandler()}
              >
                STARTED
                <SortIndicator
                  sorted={table.getColumn('createdAt')?.getIsSorted() || false}
                />
              </TableHeadCell>
              <TableHeadCell
                number
                sx={{ width: '60px', cursor: 'pointer' }}
                onClick={table.getColumn('trades')?.getToggleSortingHandler()}
              >
                TRADES
                <SortIndicator
                  sorted={table.getColumn('trades')?.getIsSorted() || false}
                />
              </TableHeadCell>
              <TableCell sx={{ width: '150px' }}>NEXT ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState>
                    <Typography
                      variant='bodyMedium'
                      sx={{
                        color: theme.color.onSurfaceVariant,
                        fontWeight: 500,
                      }}
                    >
                      No operations match your filters. Select portfolios and
                      run a rebalance, or generate a trade batch.
                    </Typography>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const op = row.original;
                const isExpanded = expandedId === op.id;
                return (
                  <Box component='tbody' key={op.id}>
                    <TableRow
                      sx={{
                        cursor: 'pointer',
                        borderLeft: '2px solid',
                        borderLeftColor: op.isActive
                          ? theme.color.primary
                          : op.isFailed
                            ? theme.color.negative
                            : op.isComplete
                              ? theme.color.positive
                              : theme.color.outlineVariant,
                        '&:hover': {
                          bgcolor: theme.color.surfaceContainerVariant,
                        },
                        transition: 'background 0.1s',
                      }}
                      onClick={() => setExpandedId(isExpanded ? null : op.id)}
                    >
                      <TableCell sx={{ width: '32px', py: 0.75 }}>
                        <Tooltip title='toggle'>
                          <IconButton
                            size='xsmall'
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(isExpanded ? null : op.id);
                            }}
                            aria-label='toggle'
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon
                                sx={{ fontSize: 16 }}
                                fontSize='inherit'
                              />
                            ) : (
                              <KeyboardArrowDownIcon
                                sx={{ fontSize: 16 }}
                                fontSize='inherit'
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: '70px', py: 0.75 }}>
                        <TypeBadge type={op.type} theme={theme} />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <StatusBadge
                          label={op.label}
                          isActive={op.isActive}
                          isFailed={op.isFailed}
                          isComplete={op.isComplete}
                          theme={theme}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '130px', py: 0.75 }}>
                        <PipelineBar
                          progress={op.progress}
                          state={op.state}
                          isActive={op.isActive}
                          isFailed={op.isFailed}
                          theme={theme}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '100px', py: 0.75 }}>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...VALUE_SX,
                            color: theme.color.onSurfaceVariant,
                          }}
                        >
                          {relativeTime(op.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell number sx={{ width: '60px', py: 0.75 }}>
                        <Typography
                          variant='labelSmall'
                          sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                        >
                          {op.tradeCount > 0 ? op.tradeCount : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ width: '150px', py: 0.75 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {op.isActive ? (
                          <Button
                            size='small'
                            variant='contained'
                            onClick={() => {
                              if (op.type === 'bulkTradeRun')
                                setExpandedId(op.id);
                              else navigateTo('/portfolioOptimizers');
                            }}
                          >
                            {op.nextAction}
                          </Button>
                        ) : (
                          <StatusBadge
                            label={op.nextAction}
                            isActive={false}
                            isFailed={op.isFailed}
                            isComplete={op.isComplete}
                            theme={theme}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <OperationDetailPanel
                        op={op}
                        onRefresh={
                          op.type === 'optimizer'
                            ? () => refetchOpts()
                            : () => refetchRuns()
                        }
                        parentPolling={hasActiveOps}
                      />
                    )}
                  </Box>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default OperationsTab;
